import type {
  ExamFormTestData,
  ExamFormDataInputAnswer,
  ExamForm,
  ExamFormDataInputRow,
} from "@rmh/defs";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import SubInput from "../inputs/SubInput";
import AdditionalCommentsInput from "../inputs/AdditionalCommentsInput";
import TestFormContainer from "../inputs/TestFormContainer";
import { getExamFormAnswer } from "../helpers/getExamFormAnswer";

interface DisplayDataInputProps {
  item: ExamFormTestData;
  examId: string;
  onInputChange?: () => void;
  setExamState?: Dispatch<SetStateAction<ExamForm[]>>;
  statusComponent: ReactNode;
  disabled: boolean;
}

function DisplayDataInput({
  item,
  onInputChange,
  examId,
  setExamState,
  statusComponent,
  disabled,
}: DisplayDataInputProps){
  const onChange =
    (subType: string, index: number) =>
    (...values: unknown[]) => {
      if (setExamState) {
        setExamState((examsMaster) => {
          const examToUpdate = examsMaster.find(
            (e) => e.examFormId === examId
          ) as ExamForm;
          const examToUpdateIndex = examsMaster.findIndex(
            (e) => e.examFormId === examId
          );
          const testToUpdate = examToUpdate.examFormTests.find(
            (e) => e.testTitle === item.testTitle
          ) as ExamFormTestData;
          const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
            (e) => e.testTitle === item.testTitle
          );

          if (!Array.isArray(testToUpdate.examFormDataInputColumnAnswers)) {
            testToUpdate.examFormDataInputColumnAnswers = new Array(
              item.examFormDataInputRows?.length
            ) as ExamFormDataInputAnswer[];
          }

          // Store based on type
          testToUpdate.examFormDataInputColumnAnswers[index] =
            getExamFormAnswer(subType, values);

          examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
          examsMaster[examToUpdateIndex] = examToUpdate;
          return [...examsMaster];
        });
      }
      if (onInputChange) {
        onInputChange();
      }
    };

  const handleAddInputChange =
    (index: number) =>
    (value: string) => {
      if (setExamState) {
        setExamState((examsMaster) => {
          const examToUpdate = examsMaster.find(
            (e) => e.examFormId === examId
          ) as ExamForm;
          const examToUpdateIndex = examsMaster.findIndex(
            (e) => e.examFormId === examId
          );
          const testToUpdate = examToUpdate.examFormTests.find(
            (e) => e.testTitle === item.testTitle
          ) as ExamFormTestData;
          const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
            (e) => e.testTitle === item.testTitle
          );

          (testToUpdate.examFormDataInputRows as ExamFormDataInputRow[])[index].text = value;

          examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
          examsMaster[examToUpdateIndex] = examToUpdate;
          return [...examsMaster];
        });
      }
      if (onInputChange) {
        onInputChange();
      }
    };

  const handleCommentChange = (value: string): void => {
    if (setExamState) {
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestData;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        testToUpdate.commentsText = value;
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };

  return (
    <TestFormContainer
      statusSlot={statusComponent}
      title={item.testTitle ?? ""}
    >
      {item.examFormDataInputRows?.map((row, index) => (
        <div
          className="mb-5 flex w-full items-end"
          key={`datainput_${row.rowTitle}`}
        >
          <div className="flex flex-1 flex-col">
            {row.rowTitle?.includes(";") ? (
              <p>{row.rowTitle.split(";")[0]} </p>
            ) : (
              <p>{row.rowTitle ? row.rowTitle : "Placeholder"}</p>
            )}

            <div className="w-3/4">
              <SubInput
                answers={
                  (item.examFormDataInputColumnAnswers ?? [])[
                    index
                  ] as ExamFormDataInputAnswer
                }
                disabled={disabled}
                onInputChange={onChange(row.examFormDataInputRowType, index)}
                row={row}
                subInputType={row.examFormDataInputRowType}
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col ">
            {Boolean(row.addInputText) && (
              <AdditionalCommentsInput
                defaultValue={row.text ?? ""}
                disabled={disabled}
                onChange={handleAddInputChange(index)}
                placeholder="Comments"
              />
            )}
          </div>
        </div>
      ))}
      {Boolean(item.comments) && (
        <AdditionalCommentsInput
          defaultValue={item.commentsText ?? ""}
          disabled={disabled}
          onChange={handleCommentChange}
        />
      )}
    </TestFormContainer>
  );
}

export default DisplayDataInput;
